import "@mdxeditor/editor/style.css";
import "./mdx-editor-overrides.css";

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  Separator,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { Alert, Box } from "@mui/material";
import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmAutolinkLiteralToMarkdown } from "mdast-util-gfm-autolink-literal";
import { mdxJsxToMarkdown } from "mdast-util-mdx-jsx";
import { toMarkdown } from "mdast-util-to-markdown";
import { useEffect, useRef, useState } from "react";
import { InputProps, useInput, useTranslate } from "react-admin";

export default function MDXEditorInput(props: InputProps) {
  const { field } = useInput(props);
  const translate = useTranslate();
  const editorRef = useRef<MDXEditorMethods | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const md = (field.value as string) || "";

      // Convert markdown to MDX-compatible format to prevent crashes
      // This handles edge cases like autolinks (<https://example.com>) that MDXEditor doesn't support
      const tree = fromMarkdown(md);
      const mdx = toMarkdown(tree, { resourceLink: true });

      setMarkdown(mdx);
      setError(null);
    } catch (err) {
      console.error("Failed to parse markdown:", err);
      setError(translate("mdx.parseError"));
      setMarkdown("");
    }
  }, [field.value, translate]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // only render once the possible field.value is known
  if (markdown === null) {
    return null;
  }

  return (
    <Box>
      <Box display={{ xs: "block", sm: "flex", width: "100%", height: "100%" }}>
        <Box flex={1}>
          <MDXEditor
            ref={editorRef}
            markdown={markdown}
            onChange={field.onChange}
            translation={(key, _, interpolations) => translate(`mdx.${key}`, interpolations)}
            className="edm-mdx-editor"
            toMarkdownOptions={{
              resourceLink: false,
              extensions: [mdxJsxToMarkdown(), gfmAutolinkLiteralToMarkdown()],
            }}
            plugins={[
              listsPlugin(),
              linkPlugin(),
              markdownShortcutPlugin(),
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    <BoldItalicUnderlineToggles />
                    <Separator />
                    <ListsToggle />
                    <Separator />
                    <CreateLink />
                    <Separator />
                    <BlockTypeSelect />
                    <Separator />
                    <UndoRedo />
                  </>
                ),
              }),
              linkDialogPlugin(),
              headingsPlugin(),
            ]}
          />
        </Box>
      </Box>
    </Box>
  );
}
